import { PayloadAction } from '@reduxjs/toolkit';
import { IActionEditorState, actionEditorActions } from 'core/slices/actionEditor';
import { selectActionEditor } from 'core/slices/actionEditor/selectActionEditor';
import { ISceneEditorState, sceneEditorActions } from 'core/slices/sceneEditor';
import { selectSceneEditor } from 'core/slices/sceneEditor/selectSceneEditor';
import { EntityTypes } from 'constants/entities';
import { selectAction, selectActionDAO } from 'core/selectors/product/action/action';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { selectActionRegister } from 'core/selectors/product/action/actionRegister';
import { IViewer, updateViewerState, ViewerState } from 'core/slices/player';
import {
  IActionRegister,
  IRegisterAction,
  registerAction,
  registerActionResult,
  registerActionState,
  RegisterResult,
  RegisterState,
} from 'core/slices/register';
import { all, call, delay, fork, put, select, takeEvery, takeLatest, take } from 'redux-saga/effects';
import { RootState } from 'types';
import { ActionEntityType, IActionBasic, IActionDAO } from 'types/domain/action';

function* registerActionStateSaga(action: PayloadAction<Partial<IRegisterAction>>) {
  if (action.payload.state === RegisterState.ENDED) {
    //@ts-ignore
    const actionDAO: IActionBasic = yield select((state: RootState) => selectAction(state, action.payload.actionId));
    const actionRegister: IActionRegister = yield select((state: RootState) =>
      selectActionRegister(state, action.payload.actionId),
    );

    if (
      actionDAO.timeLimit &&
      actionRegister &&
      !actionRegister.result &&
      actionDAO.entities.filter(ent => ent.type !== ActionEntityType.TEXT).length
    ) {
      yield put(
        yield call(registerActionResult, {
          actionId: action.payload.actionId,
          sessionAttemptId: action.payload.sessionAttemptId,
          courseProgressId: action.payload.courseProgressId,
          result: RegisterResult.TIME,
        }),
      );
    }
  }
}

function* registerActionSaga(action: PayloadAction<Partial<IRegisterAction>>) {
  const viewState: IViewer = yield select((state: RootState) => state.core.player.state.viewer);
  const feed: IActionFeed = yield select(selectActionFeed);
  const isStart = feed.feed[0] && feed.feed[0].actionId === action.payload.actionId;
  //@ts-ignore
  const actionDAO: IActionBasic = yield select((state: RootState) => selectAction(state, action.payload.actionId));
  let waiting = !isStart && viewState.state !== ViewerState.RESTING;

  while (waiting) {
    const viewStateAction: PayloadAction<IViewer> = yield take(updateViewerState);
    waiting = viewStateAction.payload.state !== ViewerState.RESTING;
  }

  yield put(
    yield call(registerActionState, {
      actionId: action.payload.actionId,
      sessionAttemptId: action.payload.sessionAttemptId,
      state: RegisterState.STARTED,
      time: actionDAO.timeLimit,
    }),
  );

  if (actionDAO.timeLimit) {
    let time = actionDAO.timeLimit;
    let paused = false;

    while (time > 0 && !paused) {
      yield delay(1000);

      const actionEditor: IActionEditorState = yield select(selectActionEditor);
      paused = actionEditor.enabled;

      const actionId = yield select((state: RootState) => state.state[EntityTypes.ACTION]);
      const actionRegister = yield select((state: RootState) => selectActionRegister(state, action.payload.actionId));

      if (actionId !== action.payload.actionId || !actionRegister) return;

      while (paused) {
        const action = yield take(actionEditorActions.enable);
        paused = action.payload;
      }

      time--;
      yield put(
        yield call(registerActionState, {
          actionId: action.payload.actionId,
          sessionAttemptId: action.payload.sessionAttemptId,
          time,
        }),
      );
    }

    yield put(
      yield call(registerActionState, {
        actionId: action.payload.actionId,
        sessionAttemptId: action.payload.sessionAttemptId,
        state: RegisterState.ENDED,
        time: 0,
      }),
    );
  }
}

function* watchRegisterActionState() {
  yield takeEvery(registerActionState, registerActionStateSaga);
}

function* watchRegisterAction() {
  yield takeEvery(registerAction, registerActionSaga);
}

export function* timerSaga() {
  yield all([fork(watchRegisterAction), fork(watchRegisterActionState)]);
}
